import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ImagePlus, Building2, Plus, X, Trash2 } from "lucide-react";

const blank = {
  name: "", facility_type: "Assisted Living Residence", facility_type_detail: "",
  address_full: "", telephone: "", county: "", licensed_beds_total: "",
  tier: "premium", description: "", amenities: "", photos: [],
  available_beds: "", contact_email: "", owner_email: "",
};

export function FacilityCreator({ prefill, onCreated }) {
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [recent, setRecent] = useState([]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (prefill) {
      setForm({ ...blank, ...prefill, tier: prefill.tier || "premium" });
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [prefill]);

  const loadRecent = async () => {
    try {
      const { data } = await api.get("/facilities", { params: { limit: 800 } });
      setRecent((data.facilities || []).filter((f) => f.created_by_admin).slice(0, 12));
    } catch {}
  };
  useEffect(() => { loadRecent(); }, []);

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); set("photos", [...form.photos, data.url]); }
    catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/facilities", {
        ...form,
        licensed_beds_total: parseInt(form.licensed_beds_total || 0, 10),
        available_beds: form.available_beds === "" ? null : parseInt(form.available_beds, 10),
        amenities: form.amenities.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success(`${form.name} added & published.`);
      onCreated?.(data);
      setForm(blank); loadRecent();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not create facility"); }
    finally { setSaving(false); }
  };

  const removeFacility = async (f) => {
    if (!window.confirm(`Delete "${f.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/facilities/${f.facility_id}`); toast.success("Facility deleted"); loadRecent(); }
    catch { toast.error("Could not delete"); }
  };

  return (
    <div className="grid lg:grid-cols-[1.5fr_1fr] gap-8">
      <form onSubmit={submit} className="space-y-4 max-w-[640px]">
        <h3 className="font-heading text-xl font-medium text-[#1C211F] flex items-center gap-2"><Building2 className="h-5 w-5 text-[#0077B6]" /> Add a facility (done-for-you)</h3>
        <div className="space-y-1.5"><Label htmlFor="nf-name">Facility name *</Label><Input id="nf-name" required data-testid="nf-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-xl" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="nf-type">Type</Label>
            <select id="nf-type" className="w-full h-10 rounded-xl border border-[#E8E6DF] px-3 bg-white" data-testid="nf-type" value={form.facility_type} onChange={(e) => set("facility_type", e.target.value)}>
              <option>Assisted Living Residence</option>
              <option>Nursing Facilities</option>
            </select>
          </div>
          <div className="space-y-1.5"><Label htmlFor="nf-detail">Type detail</Label><Input id="nf-detail" data-testid="nf-detail" value={form.facility_type_detail} onChange={(e) => set("facility_type_detail", e.target.value)} placeholder="e.g. Memory care" className="rounded-xl" /></div>
        </div>
        <div className="space-y-1.5"><Label htmlFor="nf-address">Full address *</Label><Input id="nf-address" required data-testid="nf-address" value={form.address_full} onChange={(e) => set("address_full", e.target.value)} placeholder="1234 Main St, Boulder, CO 80302" className="rounded-xl" /><p className="text-xs text-[#5C6660]">We geocode this to place it on the map.</p></div>
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1.5"><Label htmlFor="nf-phone">Phone</Label><Input id="nf-phone" data-testid="nf-phone" value={form.telephone} onChange={(e) => set("telephone", e.target.value)} className="rounded-xl" /></div>
          <div className="space-y-1.5"><Label htmlFor="nf-county">County</Label><Input id="nf-county" data-testid="nf-county" value={form.county} onChange={(e) => set("county", e.target.value)} className="rounded-xl" /></div>
          <div className="space-y-1.5"><Label htmlFor="nf-beds">Licensed beds</Label><Input id="nf-beds" type="number" data-testid="nf-beds" value={form.licensed_beds_total} onChange={(e) => set("licensed_beds_total", e.target.value)} className="rounded-xl" /></div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="nf-tier">Plan</Label>
            <select id="nf-tier" className="w-full h-10 rounded-xl border border-[#E8E6DF] px-3 bg-white" data-testid="nf-tier" value={form.tier} onChange={(e) => set("tier", e.target.value)}>
              <option value="free">Free</option><option value="basic">Basic</option><option value="premium">Premium</option>
            </select>
          </div>
          <div className="space-y-1.5"><Label htmlFor="nf-open">Beds open</Label><Input id="nf-open" type="number" data-testid="nf-open" value={form.available_beds} onChange={(e) => set("available_beds", e.target.value)} className="rounded-xl" /></div>
          <div className="space-y-1.5"><Label htmlFor="nf-contact">Inquiry email</Label><Input id="nf-contact" type="email" data-testid="nf-contact" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} className="rounded-xl" /></div>
        </div>
        <div className="space-y-1.5"><Label htmlFor="nf-desc">Description</Label><Textarea id="nf-desc" rows={3} data-testid="nf-desc" value={form.description} onChange={(e) => set("description", e.target.value)} className="rounded-xl" /></div>
        <div className="space-y-1.5"><Label htmlFor="nf-amenities">Amenities (comma separated)</Label><Input id="nf-amenities" data-testid="nf-amenities" value={form.amenities} onChange={(e) => set("amenities", e.target.value)} placeholder="Private rooms, 24/7 nursing, Garden" className="rounded-xl" /></div>
        <div className="space-y-2">
          <Label>Photos</Label>
          <div className="flex flex-wrap gap-2">
            {form.photos.map((p, i) => (
              <div key={p} className="relative">
                <img src={p} alt={`Facility photo ${i + 1}`} className="h-16 w-16 rounded-xl object-cover" />
                <button type="button" onClick={() => set("photos", form.photos.filter((_, j) => j !== i))} className="absolute -top-1.5 -right-1.5 bg-white rounded-full border border-[#E8E6DF] p-0.5"><X className="h-3 w-3" /></button>
              </div>
            ))}
            <label className="h-16 w-16 rounded-xl border-2 border-dashed border-[#E8E6DF] flex items-center justify-center cursor-pointer hover:border-[#1A365D]" data-testid="nf-photo">
              {uploading ? <Loader2 className="h-5 w-5 animate-spin text-[#5C6660]" /> : <ImagePlus className="h-5 w-5 text-[#5C6660]" />}
              <input type="file" accept="image/*" className="hidden" onChange={upload} />
            </label>
          </div>
        </div>
        <div className="space-y-1.5"><Label htmlFor="nf-owner">Assign to director (their account email) — optional</Label><Input id="nf-owner" type="email" data-testid="nf-owner" value={form.owner_email} onChange={(e) => set("owner_email", e.target.value)} placeholder="director@facility.com" className="rounded-xl" /></div>
        <Button type="submit" disabled={saving} className="bg-[#1A365D] hover:bg-[#12283F] text-white rounded-xl h-11 px-8" data-testid="create-facility-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-2" /> Add & publish facility</>}
        </Button>
      </form>

      <div>
        <h3 className="font-heading text-lg font-medium text-[#1C211F] mb-3">Recently added by you</h3>
        {recent.length === 0 ? (
          <p className="text-sm text-[#5C6660]">Facilities you add will appear here.</p>
        ) : (
          <div className="space-y-2" data-testid="admin-created-list">
            {recent.map((f) => (
              <div key={f.facility_id} className="bg-white border border-[#E8E6DF] rounded-xl p-3 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-sm text-[#1C211F] truncate">{f.name}</div>
                  <div className="text-xs text-[#5C6660]">{f.city || f.county} · plan: {f.tier}</div>
                </div>
                <button onClick={() => removeFacility(f)} className="shrink-0 text-[#C0392B] hover:bg-[#FBE6E6] rounded-lg p-1.5" data-testid="delete-facility-btn" title="Delete"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
